import { buildTaskTree } from './taskUtils';
import { parseHumanReadableDate, detectDateInText } from './taskUtils';

// Format date for export (human-readable format like "Jan 6", never "Today" or "Tomorrow")
function formatDateForExport(dueDateString) {
  if (!dueDateString) return null;
  
  const dateStr = dueDateString.split('T')[0];
  const [year, month, day] = dateStr.split('-');
  const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monthNames[dueDate.getMonth()]} ${dueDate.getDate()}`;
}

// Export tasks to text format
// Format:
// - Incomplete task: [ ]
// - Completed task: [x]
// - Waiting status task: [w]
// - Work in progress status task: [/]
// - Won't do status task: [-]
// - Tasks with subtasks: no checkbox, just the text
// - Due dates: appended in parentheses at the end, e.g., "Task text (Jan 6)"
// Parent-children relationship denoted using tabs
export function exportTasksToText(tasks) {
  const taskTree = buildTaskTree(tasks);
  let output = '';
  
  const formatTask = (task, indentLevel = 0) => {
    const indent = '\t'.repeat(indentLevel);
    const hasSubtasks = task.subtasks && task.subtasks.length > 0;
    
    // Format task text with optional due date
    let taskText = task.text || '';
    if (task.due_date) {
      const formattedDate = formatDateForExport(task.due_date);
      if (formattedDate) {
        taskText = `${taskText} (${formattedDate})`;
      }
    }
    
    let line;
    if (hasSubtasks) {
      // Tasks with subtasks don't have checkbox notation
      line = `${indent}${taskText}\n`;
    } else {
      // Tasks without subtasks have checkbox notation
      let checkbox = '[ ]';
      
      if (task.status === 'complete') {
        checkbox = '[x]';
      } else if (task.status === 'waiting') {
        checkbox = '[w]';
      } else if (task.status === 'wip') {
        checkbox = '[/]';
      } else if (task.status === 'wont_do') {
        checkbox = '[-]';
      }
      
      line = `${indent}${checkbox} ${taskText}\n`;
    }
    
    output += line;
    
    // Recursively process subtasks
    if (hasSubtasks) {
      task.subtasks.forEach(subtask => {
        formatTask(subtask, indentLevel + 1);
      });
    }
  };
  
  taskTree.forEach(task => {
    formatTask(task, 0);
  });
  
  return output;
}

// Measure indent: 1 tab = 4 units, 1 space = 1 unit (so 2 or 4 spaces map to levels consistently)
function getIndentUnits(str) {
  let units = 0;
  for (let i = 0; i < str.length; i++) {
    if (str[i] === '\t') units += 4;
    else if (str[i] === ' ') units += 1;
    else break;
  }
  return units;
}

// Infer indent step from all non-zero indent amounts (smallest = one level)
function inferIndentStep(indentUnitsList) {
  const nonzero = indentUnitsList.filter((u) => u > 0);
  if (nonzero.length === 0) return 2;
  const min = Math.min(...nonzero);
  return min >= 1 ? min : 2;
}

// Parse imported text file format.
// Supports:
// - Indent: tabs, 2 spaces, or 4 spaces (inferred from content)
// - List markers: optional "- " or "* " at line start (after indent)
// - Checkbox: [ ], [x], [w], [/], [-] with optional space inside brackets (e.g. [ ] or [])
// - Plain lines: "Task 1" or "- Task 1" without checkbox
// - Due dates: in parentheses at the end, e.g. "Task text (Jan 6)"
export function parseImportedTasks(text) {
  const lines = text.split(/\r?\n/);
  const parsedTasks = [];

  // First pass: collect indent units for each non-empty line to infer step
  const lineData = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const units = getIndentUnits(line);
    lineData.push({ line: line.trimStart(), indentUnits: units });
  }

  const indentSteps = lineData.map((d) => d.indentUnits);
  const step = inferIndentStep(indentSteps);

  for (let i = 0; i < lineData.length; i++) {
    const { line: trimmedLine, indentUnits } = lineData[i];
    const indentLevel = step > 0 ? Math.floor(indentUnits / step) : 0;
    let remainingLine = trimmedLine;

    // Strip optional list marker: "- " or "* " (dash or star followed by space)
    const listMarkerMatch = remainingLine.match(/^[-*]\s+/);
    if (listMarkerMatch) {
      remainingLine = remainingLine.slice(listMarkerMatch[0].length);
    }

    // Checkbox: [ ], [x], [w], [/], [-] with optional space inside brackets
    const checkboxMatch = remainingLine.match(/^\[\s*([ xw\/-]?)\s*\]\s*(.*)$/);
    let taskText;
    let status = 'incomplete';
    let dueDate = null;

    if (checkboxMatch) {
      const checkboxState = (checkboxMatch[1] || ' ').trim() || ' ';
      taskText = (checkboxMatch[2] || '').trim();

      if (checkboxState === 'x' || checkboxState === 'X') {
        status = 'complete';
      } else if (checkboxState === 'w' || checkboxState === 'W') {
        status = 'waiting';
      } else if (checkboxState === '/') {
        status = 'wip';
      } else if (checkboxState === '-') {
        status = 'wont_do';
      }
    } else {
      taskText = remainingLine.trim();
      status = 'incomplete';
    }

    // Optional date in parentheses at the end
    const dateMatch = taskText.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    if (dateMatch) {
      const dateStr = dateMatch[2].trim();
      const parsedDate = parseHumanReadableDate(dateStr);
      if (parsedDate) {
        taskText = dateMatch[1].trim();
        dueDate = parsedDate;
      }
    }

    // If no date from parentheses, detect date in text (e.g. "Monday", "Jan 16", "Do something on Jan 16")
    if (dueDate === null) {
      const detectedDate = detectDateInText(taskText);
      if (detectedDate) {
        dueDate = detectedDate;
      }
    }

    if (taskText !== null && taskText !== undefined) {
      parsedTasks.push({
        text: taskText || '',
        indentLevel,
        status,
        dueDate
      });
    }
  }

  // Inherit due date from parent: subtasks without a due date get the parent's due date
  for (let i = 0; i < parsedTasks.length; i++) {
    const task = parsedTasks[i];
    if (task.dueDate != null || task.indentLevel === 0) continue;
    // Find immediate parent (most recent preceding task with smaller indent)
    for (let j = i - 1; j >= 0; j--) {
      if (parsedTasks[j].indentLevel < task.indentLevel) {
        if (parsedTasks[j].dueDate != null) {
          task.dueDate = parsedTasks[j].dueDate;
        }
        break;
      }
    }
  }

  return parsedTasks;
}

